import { AfterContentInit, AfterViewChecked, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChildren, EventEmitter, Input, OnDestroy, Output, QueryList, ViewChild } from "@angular/core";
import { TabView } from "primeng/tabview";
import { Subscription } from "rxjs";
import { TabPanelComponent } from "./tab-panel.component";

@Component({
  selector: "app-tab-menu",
  templateUrl: "./tab-menu.component.html",
  styleUrls: ["./tab-menu.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TabView],
})
export class TabMenuComponent implements AfterContentInit, AfterViewInit, AfterViewChecked, OnDestroy {
  @Input() orientation = "left";
  @Output() activeIndexChange = new EventEmitter<{ index: number; header: string }>(true);

  @ViewChild(TabView, { static: true }) primeTabMenu: TabView;
  @ContentChildren(TabPanelComponent) tabPanels: QueryList<TabPanelComponent>;

  private isTabSyncPending = true;
  private tabPanelsChangeSubscription: Subscription;

  constructor(private cd: ChangeDetectorRef) {}

  ngAfterContentInit() {
    this.selectDefaultTab();
    if (this.tabPanels != null) {
      this.tabPanelsChangeSubscription = this.tabPanels.changes.subscribe(() => {
        this.selectDefaultTab();
        this.isTabSyncPending = true;
      });
    }
  }

  ngAfterViewInit() {
    this.cd.detectChanges();
  }

  ngAfterViewChecked() {
    if (this.isTabSyncPending) {
      this.isTabSyncPending = false;
      this.syncPrimeTabPanels();
    }
  }

  ngOnDestroy() {
    if (this.tabPanelsChangeSubscription != null) {
      this.tabPanelsChangeSubscription.unsubscribe();
    }
  }

  trackByIndex(index, item) {
    return index;
  }

  onTabChange(event): void {
    const activeIndex = event.index; // Get active tab index
    const header = this.primeTabMenu.tabs[activeIndex]?.header || "";
    this.activeIndexChange.emit({ index: activeIndex, header });
  }

  setActiveTab(tabName: string): void {
    if (this.primeTabMenu) {
      const tabIndex = this.primeTabMenu.tabs.findIndex(tab => tab.header === tabName);

      if (tabIndex !== -1) {
        this.primeTabMenu.tabs.forEach(tab => (tab.selected = false));
        this.primeTabMenu.tabs[tabIndex].selected  = true;
        this.cd.detectChanges();
      }
    }
  }

  private selectDefaultTab(): void {
    if (this.tabPanels == null || this.tabPanels.length === 0) {
      return;
    }

    if (!this.tabPanels.some(a => a.selected)) {
      this.tabPanels.first.selected = true;
    }
  }

  // Each real p-tabPanel lives inside its own app-tab-panel view, so PrimeNG's
  // @ContentChildren(TabPanel) query on p-tabView can never match them and TabView ends up
  // with zero tabs (an empty header <ul>). We therefore feed TabView its panels ourselves.
  //
  // This used to be done by overriding primeTabMenu.ngAfterContentInit on the instance, but
  // Ivy reads lifecycle hooks off the class prototype when it builds the directive definition
  // and invokes them as hook.call(instance), so an instance-level override is never called.
  //
  // A view hook is the right place for it: TabView's own tabPanels QueryList is not assigned
  // until its content queries are refreshed, and the app-tab-panel child views only push their
  // [header]/[selected] bindings onto the real TabPanel when they are refreshed - both of which
  // happen after content hooks but before view hooks.
  private syncPrimeTabPanels(): void {
    if (this.primeTabMenu == null || this.primeTabMenu.tabPanels == null || this.tabPanels == null) {
      return;
    }

    const primeTabPanels = this.tabPanels.map(a => a.primeTabPanel).filter(a => a != null);
    this.primeTabMenu.tabPanels.reset(primeTabPanels);
    this.primeTabMenu.initTabs();
    this.primeTabMenu.cd.detectChanges();
  }
}
